import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Zap, Gift, Loader2, Plus } from "lucide-react";

export const CustomerLoyaltyCard = ({ customer, onSaved }) => {
  const l = customer.loyalty || {};
  const [delta, setDelta] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const adjust = async () => {
    const n = parseInt(delta, 10);
    if (!n) { toast.error("Enter points to add (or a negative number to remove)"); return; }
    setSaving(true);
    try {
      const res = await api.post(`/admin/customers/${encodeURIComponent(customer.email)}/points`, { delta: n, reason });
      onSaved(res.data);
      setDelta(""); setReason("");
      toast.success("Points updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't adjust points");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="surface-card p-5 sm:p-6" data-testid="customer-loyalty-card">
      <p className="label-mono text-spark-teal flex items-center gap-1.5 mb-2"><Zap className="size-4" /> Loyalty</p>
      <div className="flex items-end justify-between gap-3">
        <p className="font-display uppercase tracking-tight text-3xl font-bold text-spark-ink" data-testid="customer-points">{l.points ?? 0} <span className="text-sm text-spark-slate">pts</span></p>
        {l.discount_pct_available > 0 && <span className="px-2 py-0.5 rounded-sm bg-spark-yellow text-spark-ink text-[10px] uppercase tracking-wider font-bold">{l.discount_pct_available}% off available</span>}
      </div>
      <p className="text-xs text-spark-slate mt-1 flex items-center gap-1.5"><Gift className="size-3.5" /> Code <code className="font-bold text-spark-teal-dark" data-testid="customer-referral-code">{l.referral_code || "—"}</code> · {l.referrals_count || 0} referral{l.referrals_count === 1 ? "" : "s"}</p>
      {l.history?.length > 0 && (
        <ul className="mt-3 max-h-40 overflow-y-auto divide-y divide-spark-line text-xs border-t border-spark-line" data-testid="customer-points-history">
          {l.history.map((h) => (
            <li key={h.id} className="py-1.5 flex items-center justify-between gap-2">
              <span className="truncate text-spark-slate">{h.reason}</span>
              <span className={`font-mono font-bold shrink-0 ${h.delta >= 0 ? "text-emerald-600" : "text-red-600"}`}>{h.delta > 0 ? "+" : ""}{h.delta}</span>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-3 pt-3 border-t border-spark-line grid grid-cols-3 gap-2">
        <input type="number" value={delta} onChange={(e) => setDelta(e.target.value)} placeholder="+/- pts" className="input-style text-sm" data-testid="points-adjust-delta" />
        <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason" className="input-style text-sm col-span-2" data-testid="points-adjust-reason" />
      </div>
      <button onClick={adjust} disabled={saving} className="btn-primary w-full mt-2 text-xs" data-testid="points-adjust-save">
        {saving ? <Loader2 className="size-4 animate-spin" /> : <Plus className="size-4" />} Adjust points
      </button>
    </div>
  );
};
